import {
  gql,
  type QueryHookOptions,
  type TypedDocumentNode,
  useQuery,
} from '@apollo/client'
import type { CategoryConnectionData } from './types'

interface CategoryConnectionVariables {
  handle?: string
  id?: string
  first?: number
  after?: string
}

const GET_CATEGORY: TypedDocumentNode<
  CategoryConnectionData,
  CategoryConnectionVariables
> = gql`
  query Collection($handle: String, $id: ID, $first: Int, $after: String) {
    collection(handle: $handle, id: $id) {
      id
      title
      handle
      description
      squaredBannerImage: metafield(
        namespace: "custom"
        key: "squared_banner_image"
      ) {
        reference {
          ... on MediaImage {
            image {
              url(transform: { maxWidth: 400 })
            }
          }
        }
      }
      products(first: $first, after: $after) {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            id
            handle
            title
            productType
            media(first: 50) {
              edges {
                node {
                  ... on MediaImage {
                    image {
                      url
                    }
                  }
                }
              }
            }
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
              maxVariantPrice {
                amount
                currencyCode
              }
            }
            compareAtPriceRange {
              minVariantPrice {
                amount
                currencyCode
              }
              maxVariantPrice {
                amount
                currencyCode
              }
            }
            color: metafield(namespace: "custom", key: "color") {
              value
            }
            colorShort: metafield(namespace: "custom", key: "color_short") {
              value
            }
            material: metafield(namespace: "custom", key: "material") {
              value
            }
            materialShort: metafield(
              namespace: "custom"
              key: "material_short"
            ) {
              value
            }
            reviewsCount: metafield(
              namespace: "stamped"
              key: "reviews_count"
            ) {
              value
            }
            reviewsAverage: metafield(
              namespace: "stamped"
              key: "reviews_average"
            ) {
              value
            }
            variantColors: metafield(
              namespace: "custom"
              key: "variant_colors"
            ) {
              value
            }
            variantColorStyle: metafield(
              namespace: "custom"
              key: "variant_color_style"
            ) {
              value
            }
            soleColorHex: metafield(
              namespace: "custom"
              key: "sole_color_hex"
            ) {
              value
            }
            textureImageName: metafield(
              namespace: "custom"
              key: "texture_image_name"
            ) {
              value
            }
            variantImage: metafield(
              namespace: "custom"
              key: "pattern_image_name"
            ) {
              value
            }
            staffPickIconID: metafield(
              namespace: "custom"
              key: "staff_pick_icon_id"
            ) {
              value
            }
            isNew: metafield(namespace: "custom", key: "is_new") {
              value
            }
            frontImageIndex: metafield(
              namespace: "custom"
              key: "front_image_index"
            ) {
              value
            }
            hoverImageIndex: metafield(
              namespace: "custom"
              key: "hover_image_index"
            ) {
              value
            }
            collectionBrandID: metafield(
              namespace: "custom"
              key: "collection_brand_id"
            ) {
              value
            }
            collectionBrandName: metafield(
              namespace: "custom"
              key: "collection_brand_name"
            ) {
              value
            }
            seasonID: metafield(namespace: "custom", key: "season_id") {
              value
            }
            seasonName: metafield(namespace: "custom", key: "season_name") {
              value
            }
            customProductType: metafield(
              namespace: "custom"
              key: "product_type"
            ) {
              value
            }
            jolly: metafield(namespace: "custom", key: "jolly") {
              value
            }
          }
        }
      }
    }
  }
`

export default function useCategory(
  options: QueryHookOptions<
    CategoryConnectionData,
    CategoryConnectionVariables
  >,
) {
  const { loading, data, error, fetchMore } = useQuery(GET_CATEGORY, {
    ...options,
    variables: { first: 100, ...options.variables },
  })

  return { loading, data, error, fetchMore }
}
